import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Header } from "../components/Header";
import { BLOG_POSTS, getPost, AUTHOR, REVIEWER } from "../content/blogPosts";
import { ArrowRight, Clock, ChevronLeft } from "lucide-react";

const SITE = "https://www.thecognitivemetrics.com";

const Block = ({ b }) => {
  if (b.t === "h2") return <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight mt-10 mb-4">{b.text}</h2>;
  if (b.t === "p") return <p className="text-slate-300 leading-relaxed mb-4 text-[15px] sm:text-base">{b.text}</p>;
  if (b.t === "quote") return (
    <blockquote className="my-6 border-l-2 border-cyan-500/50 pl-5 text-lg text-slate-200 italic">{b.text}</blockquote>
  );
  if (b.t === "ul") return (
    <ul className="mb-4 space-y-2">
      {b.items.map((it, i) => (
        <li key={i} className="flex gap-3 text-slate-300 leading-relaxed text-[15px] sm:text-base">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
  if (b.t === "ol") return (
    <ol className="mb-4 space-y-2.5 list-none">
      {b.items.map((it, i) => (
        <li key={i} className="flex gap-3 text-slate-300 leading-relaxed text-[15px] sm:text-base">
          <span className="shrink-0 w-6 h-6 rounded-full bg-cyan-500/15 text-cyan-300 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
          <span>{it}</span>
        </li>
      ))}
    </ol>
  );
  return null;
};

export const BlogPost = () => {
  const { slug } = useParams();
  const post = getPost(slug);

  useEffect(() => {
    if (!post) return;
    const url = `${SITE}/blog/${post.slug}`;
    document.title = post.metaTitle;
    const descEl = document.querySelector('meta[name="description"]');
    if (descEl) descEl.setAttribute("content", post.metaDescription);
    const kwEl = document.querySelector('meta[name="keywords"]');
    const prevKw = kwEl ? kwEl.getAttribute("content") : null;
    if (kwEl) kwEl.setAttribute("content", post.keywords);
    const canon = document.querySelector('link[rel="canonical"]');
    const prevCanon = canon ? canon.getAttribute("href") : null;
    if (canon) canon.setAttribute("href", url);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "blog-post-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.metaDescription,
          "image": post.heroImage,
          "datePublished": post.date,
          "dateModified": post.date,
          "author": { "@type": "Person", "name": AUTHOR.name, "jobTitle": AUTHOR.title, "description": AUTHOR.bio },
          "reviewedBy": { "@type": "Person", "name": REVIEWER.name, "jobTitle": REVIEWER.title },
          "publisher": { "@type": "Organization", "name": "The Cognitive Metrics", "logo": { "@type": "ImageObject", "url": `${SITE}/og-image.jpg` } },
          "mainEntityOfPage": { "@type": "WebPage", "@id": url },
          "keywords": post.keywords,
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE}/` },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${SITE}/blog` },
            { "@type": "ListItem", "position": 3, "name": post.title, "item": url },
          ],
        },
      ],
    });
    document.head.appendChild(ld);

    return () => {
      const el = document.getElementById("blog-post-ld");
      if (el) el.remove();
      if (kwEl && prevKw !== null) kwEl.setAttribute("content", prevKw);
      if (canon && prevCanon !== null) canon.setAttribute("href", prevCanon);
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-[#0B0F17]" data-testid={`blog-post-${post.slug}`}>
      <Header />
      <article className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-400 transition-colors mb-6">
          <ChevronLeft className="w-4 h-4" /> All articles
        </Link>

        <div className="flex items-center gap-3 text-xs text-slate-500 font-mono-data mb-3">
          <span>{new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]">{post.title}</h1>

        <div className="flex items-center gap-3 mt-5" data-testid="blog-byline">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-500 flex items-center justify-center text-white text-sm font-bold shrink-0">{AUTHOR.initials}</div>
          <div className="text-sm leading-tight">
            <div className="text-slate-200 font-medium">By {AUTHOR.name}</div>
            <div className="text-slate-500 text-xs">{AUTHOR.title} · Medically reviewed by {REVIEWER.name}</div>
          </div>
        </div>

        <div className="relative mt-8 rounded-3xl overflow-hidden border border-white/10">
          <img src={post.heroImage} alt={post.heroAlt} className="w-full h-56 sm:h-72 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/60 to-transparent" />
        </div>

        <div className="mt-9">
          {post.sections.map((sec, si) => (
            <section key={si}>
              {sec.body.map((b, bi) => <Block key={bi} b={b} />)}
            </section>
          ))}
        </div>

        {/* inline CTA */}
        <div className="mt-12 p-7 rounded-3xl border border-cyan-500/30 bg-gradient-to-b from-[#182234] to-[#0F172A] text-center">
          <h2 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight">Ready to see your score?</h2>
          <p className="mt-2 text-slate-400 text-sm sm:text-base">Take the credited 30-question IQ test and get your exact score, percentile and cognitive breakdown in ~25 minutes.</p>
          <Link to="/" data-testid="blog-post-cta" className="mt-5 inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold px-6 py-3.5 rounded-xl hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all">
            Take the IQ test <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* author bio */}
        <div className="mt-12 p-6 rounded-3xl bg-[#131B2A] border border-white/10 flex gap-4" data-testid="author-bio">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-500 flex items-center justify-center text-white font-bold shrink-0">{AUTHOR.initials}</div>
          <div>
            <div className="font-semibold text-slate-100">{AUTHOR.name}</div>
            <div className="text-xs text-cyan-400 mb-1.5">{AUTHOR.title}</div>
            <p className="text-sm text-slate-400 leading-relaxed">{AUTHOR.bio}</p>
            <p className="text-xs text-slate-500 mt-2">Reviewed for accuracy by {REVIEWER.name}, {REVIEWER.title}.</p>
          </div>
        </div>

        {/* related */}
        <div className="mt-12">
          <h2 className="font-heading text-xl font-bold text-white mb-4">Keep reading</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`related-${p.slug}`} className="group p-5 rounded-2xl bg-[#131B2A] border border-white/10 hover:border-cyan-500/40 transition-colors">
                <h3 className="font-semibold text-slate-100 group-hover:text-cyan-300 transition-colors leading-snug">{p.title}</h3>
                <p className="text-sm text-slate-400 mt-1.5">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
};
